import React from "react";
import { Box, Container, Divider, Grid, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getAmitasoftSiteUrls } from "../../utils/amitasoftSiteUrls";
import { AMITASOFT_BRAND } from "../../theme/amitasoftBrand";
import { AMITASOFT_TYPO } from "../../theme/amitasoftTypography";

const linkSx = {
  color: AMITASOFT_BRAND.textMuted,
  fontFamily: "Inter, sans-serif",
  fontSize: { xs: "0.875rem", md: "1rem" },
  textDecoration: "none",
  "&:hover": { opacity: 0.75 },
};

const contactLinkSx = {
  ...linkSx,
                color: AMITASOFT_BRAND.textMuted,
};

function Footer() {
  const siteUrls = getAmitasoftSiteUrls();
  const year = new Date().getFullYear();

  const quickLinks = [
    { label: "Home", href: siteUrls.marketingHome, external: true },
    { label: "About Us", href: siteUrls.aboutUs, external: true },
    { label: "Our Products", href: siteUrls.products, external: true },
    { label: "Blogs", href: siteUrls.blogHome, external: false },
    { label: "Contact Us", href: siteUrls.contactUs, external: true },
    { label: "Schedule Demo", href: siteUrls.scheduleDemo, external: true },
    { label: "FAQ", href: siteUrls.faq, external: true },
  ];

  const legalLinks = [
    { label: "Terms & Conditions", href: siteUrls.termsConditions, external: true },
    { label: "Privacy Policy", href: siteUrls.privacyPolicy, external: true },
  ];

  const socialLinks = [
    {
      name: "LinkedIn",
      href: siteUrls.social.linkedin,
      icon: `${process.env.PUBLIC_URL}/images/social/linkdin-icon.svg`,
    },
    {
      name: "Instagram",
      href: siteUrls.social.instagram,
      icon: `${process.env.PUBLIC_URL}/images/social/instagram-icon.svg`,
    },
    {
      name: "Facebook",
      href: siteUrls.social.facebook,
      icon: `${process.env.PUBLIC_URL}/images/social/facebook-icon.svg`,
    },
    {
      name: "X",
      href: siteUrls.social.twitter,
      icon: `${process.env.PUBLIC_URL}/images/social/x-icon.svg`,
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: AMITASOFT_BRAND.footerPartnerBg,
        borderTop: `1px solid ${AMITASOFT_BRAND.footerBorder}`,
        mt: 0,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: { xs: 7, md: 9 }, pb: { xs: 6, md: 8 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography
              sx={{
                fontFamily: AMITASOFT_TYPO.fontFamily,
                ...AMITASOFT_TYPO.section,
                fontWeight: 400,
                background: AMITASOFT_BRAND.gradientPartner,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Partner with us
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                color: AMITASOFT_BRAND.textMuted,
                fontSize: { xs: "0.875rem", md: "1rem" },
                mb: 1.5,
              }}
            >
              Quick Links
            </Typography>
            <Stack spacing={1.2}>
              {quickLinks.map((item) =>
                item.external ? (
                  <Link
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={linkSx}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <Link key={item.label} component={RouterLink} to={item.href} sx={linkSx}>
                    {item.label}
                  </Link>
                )
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Stack spacing={2.5}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    color: AMITASOFT_BRAND.textMuted,
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    mb: 0.5,
                  }}
                >
                  Email
                </Typography>
                <Link href={`mailto:${AMITASOFT_BRAND.contactEmail}`} sx={contactLinkSx}>
                  {AMITASOFT_BRAND.contactEmail}
                </Link>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    color: AMITASOFT_BRAND.textMuted,
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    mb: 0.5,
                  }}
                >
                  Phone Number
                </Typography>
                <Link href={`tel:${AMITASOFT_BRAND.contactPhone}`} sx={contactLinkSx}>
                  {AMITASOFT_BRAND.contactPhone}
                </Link>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    color: AMITASOFT_BRAND.textMuted,
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    mb: 0.5,
                  }}
                >
                  Social
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  {socialLinks.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.name}
                      sx={{
                        display: "inline-flex",
                        flexShrink: 0,
                        lineHeight: 0,
                        "&:hover": { opacity: 0.7 },
                      }}
                    >
                      <Box
                        component="img"
                        src={item.icon}
                        alt={item.name}
                        sx={{
                          width: { xs: 40, lg: 48 },
                          height: { xs: 40, lg: 48 },
                          objectFit: "contain",
                        }}
                      />
                    </Link>
                  ))}
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Typography
          align="center"
          sx={{ color: AMITASOFT_BRAND.textMuted, fontFamily: "Inter, sans-serif", fontSize: "0.875rem", mb: 2 }}
        >
          © {year} {AMITASOFT_BRAND.siteName}. All rights reserved.
        </Typography>

        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{ color: AMITASOFT_BRAND.textMuted, fontFamily: "Inter, sans-serif", fontSize: "0.875rem" }}
        >
          {legalLinks.map((item, index) => (
            <React.Fragment key={item.label}>
              <Link href={item.href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                {item.label}
              </Link>
              {index < legalLinks.length - 1 && <Typography component="span">|</Typography>}
            </React.Fragment>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

export default Footer;
